import { LitElement } from 'lit';
import Glider from 'glider-js';
import { registerAutoplay } from './lib/autoplay';

/**
 * A responsive slider web component built on top of Glider.js.
 * Initialises lazily when the element becomes visible in the viewport,
 * and refreshes its layout whenever it becomes visible again (e.g. inside
 * an accordion or tabs panel that was previously hidden).
 *
 * @element toujou-slider
 *
 * @fires toujou-slider-ready - Fired once the slider has been initialised.
 *   Detail: `{ slider: Glider }`.
 */
class ToujouSlider extends LitElement {
  static get is() {
    return 'toujou-slider';
  }

  static get properties() {
    return {
      breakpoints: {
        type: Array,
      },
      slidesToShow: {
        type: Number,
        attribute: 'slides-to-show',
      },
      slidesToScroll: {
        type: Number,
      },
      draggable: {
        type: Boolean,
      },
      rewind: {
        type: Boolean,
      },
      autoPlay: {
        type: Number,
        attribute: 'auto-play',
      },
    };
  }

  constructor() {
    super();

    /** CSS selectors used to find child elements inside the slider markup. */
    this.settings = {
      prevControlSelector: '.slider__control--prev',
      nextControlSelector: '.slider__control--next',
      dotsSelector: '.slider__pagination',
      sliderFrameSelector: '.slider__frame',
    };

    this.slidesToShow = 1;
    this.slidesToScroll = 1;
    this.draggable = true;
    this.rewind = true;

    /** @type {Number} */
    this.autoPlay = null;

    /** @type {Array|undefined} */
    this.breakpoints = undefined;

    /** @type {Glider|null} */
    this._slider = null;

    /** @type {IntersectionObserver|null} */
    this._observer = null;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    this._observer = new IntersectionObserver((entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        if (!this._slider) {
          this._initSlider();
        } else {
          // Re-calculate slide dimensions after being hidden and re-shown
          this._slider.refresh(true);
        }
      }
    }, {
      root: null,
      threshold: 0.001,
    });

    this._observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this._destroyObserver();
  }

  updated(_changedProperties) {
    super.updated(_changedProperties);

    if (this._slider == null) {
      return;
    }
    const sliderOptions = this._sliderOptions;
    this._slider.setOption(this._sliderOptions, typeof sliderOptions.responsive !== 'undefined');
  }

  _initSlider() {
    if (this._slider) return;

    const frame = this.querySelector(this.settings.sliderFrameSelector);
    if (!frame) return;

    this._slider = new Glider(frame, {
      ...this._sliderOptions,
      arrows: {
        prev: this.querySelector(this.settings.prevControlSelector),
        next: this.querySelector(this.settings.nextControlSelector),
      },
      dots: this.querySelector(this.settings.dotsSelector),
    });

    if (this.autoPlay !== null) {
      registerAutoplay(this._slider, this, this.autoPlay);
    }

    this.dispatchEvent(new CustomEvent('toujou-slider-ready', {
      detail: { slider: this._slider },
      bubbles: true,
      composed: true,
    }));
  }

  get _sliderOptions() {
    return {
      slidesToShow: this.slidesToShow,
      slidesToScroll: this.slidesToScroll,
      draggable: this.draggable,
      rewind: this.rewind,
      scrollLock: true,
      resizeLock: true,
      responsive: this.breakpoints,
    };
  }

  _destroyObserver() {
    if (!this._observer) return;

    this._observer.disconnect();
    this._observer = null;
  }
}
customElements.define(ToujouSlider.is, ToujouSlider);
